<template>
    <div class="relative -mx-4 border-t-4 border-secondary" :class="[config.activeChristmasStyles ? 'bg-christmas-blur' : 'bg-blur']">
        <div class="relative z-20 py-6 lg:py-12 border-t-[12px]" :class="[config.activeChristmasStyles ? 'border-christmas' : 'border-hint']">
            <div class="container m-auto flex flex-col gap-6">
                <div class="flex flex-col items-center text-center">
                    <div class="text-2xl text-headline font-semibold">{{ t('newsletter.cms.title') }}</div>
                    <p class="text-lg">{{ t('newsletter.cms.subtitle') }}</p>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-3 md:max-w-[800px] md:m-auto md:gap-12">
                    <template v-if="config.activeChristmasStyles">
                        <div class="flex gap-4 md:gap-3 text-headline items-center">
                            <MiscIcon name="winter-specials" class="!w-20 !h-20"/>
                            <span class="text-xl font-semibold">{{ t('newsletter.cms.benefits.benefitChristmas1') }}</span>
                        </div>
                    </template>
                    <template v-else>
                        <div class="flex gap-4 md:gap-3 text-headline items-center">
                            <MiscIcon name="shopping-cart-full" class="!w-20 !h-20"/>
                            <span class="text-xl font-semibold">{{ t('newsletter.cms.benefits.benefit1') }}</span>
                        </div>
                    </template>

                    <template v-if="config.activeChristmasStyles">
                        <div class="flex gap-4 md:gap-3 text-headline items-center">
                            <MiscIcon name="present" class="!w-20 !h-20 !min-w-20"/>
                            <span class="text-xl font-semibold">{{ t('newsletter.cms.benefits.benefitChristmas2') }}</span>
                        </div>
                    </template>
                    <template v-else>
                        <div class="flex gap-4 md:gap-3 text-headline items-center">
                            <MiscIcon name="gutscheine" class="!w-20 !h-20"/>
                            <span class="text-xl font-semibold">{{ t('newsletter.cms.benefits.benefit2') }}</span>
                        </div>
                    </template>

                    <div class="flex gap-4 md:gap-3 text-headline items-center">
                        <MiscIcon name="promotions" class="!w-20 !h-20"/>
                        <span class="text-xl font-semibold">{{ t('newsletter.cms.benefits.benefit3') }}</span>
                    </div>
                </div>

                <div class="w-full max-w-[800px] m-auto">
                    <form ref="newsletterForm" class="w-full form-control" @submit.prevent="submitForm">
                        <div class="flex flex-col md:flex-row gap-2 md:gap-0 w-full">
                            <label for="applyCoupon" class="sr-only">{{ t('newsletter.form.email.label') }}</label>
                            <input
                                v-model="email"
                                type="email"
                                inputmode="email"
                                :placeholder="t('newsletter.form.email.placeholder')"
                                class="grow w-full input input-bordered md:rounded-e-none md:border-e-0"
                                required
                            >
                            <button class="btn btn-secondary w-full md:w-auto md:rounded-s-none" type="submit">
                                <span>{{ t('newsletter.form.submit') }}</span>
                                <MiscIcon name="chevron-right"/>
                            </button>
                        </div>

                        <div class="p-3">
                            <label for="newsletter-privacy" class="label cursor-pointer items-start">
                                <input
                                    id="newsletter-privacy"
                                    type="checkbox"
                                    class="checkbox checkbox-secondary bg-white mr-4 mt-1"
                                    value="true"
                                    required>

                                <i18n-t
                                    keypath="newsletter.form.privacy"
                                    tag="div"
                                    class="mr-auto"
                                    scope="global"
                                >
                                    <button
                                        type="button"
                                        class="link"
                                        @click="openModal(session?.salesChannelCustomFields?.custom_checkout_footer_privacy)"
                                        @keydown.enter="openModal(session?.salesChannelCustomFields?.custom_checkout_footer_privacy)"
                                    >
                                        {{ t('newsletter.form.privacyLinkText') }}
                                    </button>
                                </i18n-t>
                            </label>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { hblUseForm } from '@/utils/helper'
import { NewsletterShopware } from "@hubblecommerce/hubble/platforms/shopware/api-client"
import { useI18n } from 'vue-i18n'
import { useNotification } from '#layers/hubble/composables/useNotification'
import {useModal} from "~/composables/useModal";
import {useCms} from "~/composables/useCms";
import {usePlatform} from "#layers/hubble/composables/usePlatform";
import {storeToRefs} from "pinia";

const config = useRuntimeConfig().public
const { t } = useI18n()
const router = useRouter()

const email = ref('')

const { validateForm } = hblUseForm()
const newsletterForm = ref()

const { showNotification } = useNotification()
const { recaptcha } = useRecaptchaV3()

const { toggleModal } = useModal()
const { cmsData, fetchCms } = useCms()

const platformStore = usePlatform()
const { session } = storeToRefs(platformStore)

async function submitForm() {
    try {
        const isValid = await validateForm(newsletterForm.value)

        if (!isValid) {
            return
        }

        const isNotABot = await recaptcha()
        if (!isNotABot) {
            console.error('Recaptcha failed, please try again.')
            return
        }

        const requestBody = {
            email: email.value,
            option: 'subscribe',
            storefrontUrl: import.meta.client ? window.location.origin : ''
        }

        await NewsletterShopware.subscribeToNewsletter(requestBody)

        // Weiterleitung zur /newsletter Seite mit der E-Mail-Adresse als Query-Parameter
        router.push({ path: '/newsletter', query: { email: email.value } })

        showNotification(t('newsletter.form.success'), 'success')
    } catch (e) {
        showNotification(t('newsletter.form.error'), 'error', true)
    }
}

const openModal = async function(id: any) {
    await fetchCms(id)

    toggleModal('cms', cmsData.value)
}
</script>
